import React, { useMemo, createRef, useState, useCallback } from "react";
import { Formik } from "formik";
import { useParams, useNavigate } from "react-router";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Typography from "@mui/material/Typography";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import MiscRates from "./MiscRates";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import every from "lodash/every";
import sortBy from "lodash/sortBy";
import isEqual from "lodash/isEqual";
import {
  UPDATE_PROJECT_SETTINGS_MUTATION,
  UPDATE_COST_CENTER_MUTATION,
  CALCULATE_ALL_BILLING_MUTATION,
} from "../../../constants/graphql/mutations";
import useAuth from "../../../hooks/useAuth";
import useMutation from "../../../hooks/useMutation";
import useNotification from "../../../hooks/notification";
import useUnsavedChanges from "../../../hooks/useUnsavedChanges";
import useQueryParams from "../../../hooks/useQueryParams";
import { useProject } from "../ProjectProvider";
import BillingTypes from "./BillingTypes";
import TaxSettings from "./TaxSettings";
import CostCenterModal from "./CostCenterModal";
import UnsavedChangesDialog from "../../modals/UnsavedChanges/UnsavedChangesDialog";

const styles = {
  dialogContent: {
    padding: 0,
    display: "flex",
    flexDirection: "column",
    height: "800px",
  },
  tabsContainer: {
    position: "sticky",
    top: 0,
    zIndex: 1,
    backgroundColor: "white",
    borderBottom: "1px solid #e0e0e0",
    mx: 2,
  },
  tabs: {
    "& .MuiTab-root": {
      textTransform: "none",
      fontSize: 16,
      fontWeight: 400,
    },
  },
  content: {
    flexGrow: 1,
    p: 2,
    pt: 1,
    overflow: "auto",
    display: "flex",
    flexDirection: "column",
  },
};

const CONFIG_KEYS = [
  "billingRates",
  "laborRates",
  "materialRates",
  "openInspectRates",
  "equipmentRates",
  "billingTypes",
];

const formikRef = createRef();

export default function BillingConfigurationDialog({ onClose, readOnly = false, projectId: propsProjectId }) {
  const [openCostCenterModal, setOpenCostCenterModal] = useState(false);
  const { projectId: routeProjectId } = useParams();
  const projectId = propsProjectId || routeProjectId;
  const queryParams = useQueryParams();
  const queryString = queryParams.toString();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { settings } = user.company;
  const { project } = useProject();

  const activeTab = queryParams.get("billingConfig") || "billing-types";
  const isEditRatesMode = !!queryParams.get("returnTo");
  const billingSection = queryParams.get("billingSection") || "";

  const setActiveTab = useCallback(
    newTab => {
      const newParams = new URLSearchParams(queryString);
      newParams.set("billingConfig", String(newTab));
      const search = newParams.toString();
      navigate({
        search: search ? `?${search}` : "",
      }, { replace: true });
    },
    [navigate, queryString]
  );

  const { notifySuccess, notifyError } = useNotification();
  const [updateProjectSettings] = useMutation(UPDATE_PROJECT_SETTINGS_MUTATION);
  const [updateCostCenter] = useMutation(UPDATE_COST_CENTER_MUTATION);
  const [calculateAllBilling] = useMutation(CALCULATE_ALL_BILLING_MUTATION);

  const {
    attemptClose,
    confirmDiscard,
    cancelDiscard,
    confirmSave,
    showDialog,
    saving,
  } = useUnsavedChanges({
    onClose,
    isDirtyFn: () => !readOnly && !!formikRef.current?.dirty,
    onSave: async () => {
      const submitFn = formikRef.current?.submitForm;
      submitFn();
    },
  });

  const items = useMemo(() => {
    const costCenters = project?.costCenters || [];
    const filteredCostCenters = costCenters.filter(
      cc => cc.useProjectBillingConfiguration === false
    );

    if (filteredCostCenters.length === 0 && costCenters.length > 0) {
      return [
        { id: "project", label: `Master PO Contract`, type: "project" },
        {
          id: "all-master",
          label: `All cost centers use master contract billing`,
          type: "message",
        },
      ];
    }

    return [
      { id: "project", label: `Master PO Contract`, type: "project" },
      ...sortBy(
        filteredCostCenters.map(cc => ({
          id: cc.id,
          label: cc.costCenterName,
          type: "costCenter",
          entity: cc,
        })),
        item => (item?.label || "").toLowerCase()
      ),
    ];
  }, [project]);

  const renderDropdownComponent = () => {
    return (
      <Box display="flex" alignItems="center" gap={1}>
        {/*
          <Typography variant="body1" color="text.primary">
            Viewing billing for:
          </Typography>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <Select
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
              displayEmpty
            >
              {items.map(item => (
                <MenuItem key={item.id} value={item.id} disabled={item.type === "message"}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        */}
      </Box>
    );
  };

  const [selectedId, setSelectedId] = useState("project");
  const selected = useMemo(() => items.find(i => i.id === selectedId) || items[0], [
    items,
    selectedId,
  ]);

  const tabs = [
    { label: "Billing Types", value: "billing-types" },
    { label: "Ancillary Rates", value: "ancillary-rates" },
    { label: "Advanced", value: "advanced" },
  ];

  const initialValues = useMemo(() => {
    const configIsEmpty = entity => every(CONFIG_KEYS, key => isEmpty(get(entity, key)));

    const entities = {};

    entities.project = CONFIG_KEYS.reduce((acc, key) => {
      acc[key] = get(project, key) || {};
      return acc;
    }, {});

    (project?.costCenters || []).forEach(cc => {
      const candidate = cc || {};
      const shouldCopyFromProject = configIsEmpty(candidate);
      const source = shouldCopyFromProject ? project : candidate;
      entities[cc.id] = CONFIG_KEYS.reduce((acc, key) => {
        acc[key] = get(source, key) || {};
        return acc;
      }, {});
    });

    return {
      entities,
      taxableChargeTypes: project?.resolvedTaxableChargeTypes || [],
      storageCalculationMethod: project?.resolvedStorageCalculationMethod || "highmark",
      monthlyValuationFee: project?.monthlyValuationFee ?? 0,
      billingQuantityCalculationMode: project?.billingQuantityCalculationMode || {},
      billingRules: project?.billingRules || {},
      colocationBillingEnabled: project?.colocationBillingEnabled ?? true,
      useMinimumChargeForStorage: project?.useMinimumChargeForStorage ?? false,
    };
  }, [project]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    if (readOnly) return;

    try {
      const entities = values.entities || {};

      const projectPayload = CONFIG_KEYS.reduce((acc, key) => {
        acc[key] = entities.project?.[key] || {};
        return acc;
      }, {});

      const promises = [];

      promises.push(
        updateProjectSettings({
          variables: {
            projectId,
            ...projectPayload,
            taxableChargeTypes: values.taxableChargeTypes,
            storageCalculationMethod: values.storageCalculationMethod,
            monthlyValuationFee: values.monthlyValuationFee === "" ? 0 : Number(values.monthlyValuationFee),
            billingQuantityCalculationMode: values.billingQuantityCalculationMode,
            billingRules: values.billingRules,
            colocationBillingEnabled: values.colocationBillingEnabled,
            useMinimumChargeForStorage: values.useMinimumChargeForStorage,
          },
        })
      );

      // Only update cost centers that have useProjectBillingConfiguration = false
      const costCentersToUpdate =
        project?.costCenters?.filter(cc => cc.useProjectBillingConfiguration === false) || [];

      costCentersToUpdate.forEach(costCenter => {
        const entityValues = entities[costCenter.id];
        if (entityValues) {
          const ccPayload = CONFIG_KEYS.reduce((acc, key) => {
            acc[key] = entityValues?.[key] || {};
            return acc;
          }, {});

          promises.push(
            updateCostCenter({
              variables: { id: costCenter.id, ...ccPayload },
            })
          );
        }
      });

      const results = await Promise.all(promises);

      const hasErrors = results.some(res => {
        const key = Object.keys(res?.data || {})[0];
        const op = res?.data?.[key];
        return Boolean(op?.errors && op.errors.length);
      });

      if (hasErrors) {
        notifyError("Some updates failed. Please review and try again.");
      } else {
        notifySuccess("Billing configuration has been updated");
        
        if (!onlyColocationBillingChanged(values)) {
          // Automatically recalculate billing after configuration changes. A pure
          // co-location toggle is handled by Project's after_commit storage recalc.
          calculateAllBilling({
            variables: {
              projectId,
              repairInvalidShipmentBillingTypes: true,
              syncStorageAssetBillingTypes: true,
            },
          }).catch(error => {
            console.error("Failed to trigger billing recalculation:", error);
          });
        }
        
        onClose();
      }
    } catch (error) {
      notifyError("Failed to update billing configuration");
      console.error("Failed to update billing configuration:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const onlyColocationBillingChanged = values => {
    const {
      colocationBillingEnabled: nextColocationBillingEnabled,
      ...nextValues
    } = values;
    const {
      colocationBillingEnabled: previousColocationBillingEnabled,
      ...previousValues
    } = initialValues;

    return nextColocationBillingEnabled !== previousColocationBillingEnabled &&
      isEqual(nextValues, previousValues);
  };

  return (
    <Formik
      innerRef={formikRef}
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleSubmit}
    >
      {({ handleSubmit, isSubmitting }) => {
        return (
          <Dialog
            open
            onClose={attemptClose}
            maxWidth={false}
            fullWidth
            PaperProps={{
              sx: {
                backgroundColor: "#f4f6f8",
                width: "780px",
                maxWidth: "90vw",
              },
            }}
          >
            <DialogTitle sx={{ pb: 1 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box width={40} />
                <Typography color="textSecondary" fontSize={26} fontWeight={500}>
                  Billing Configuration
                </Typography>
                <IconButton sx={{ mr: 2 }} color="textPrimary" onClick={attemptClose}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent sx={styles.dialogContent}>
              {!isEditRatesMode && (
                <Box sx={styles.tabsContainer}>
                  <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    sx={styles.tabs}
                    indicatorColor="primary"
                    textColor="primary"
                  >
                    {tabs.map(tab => (
                      <Tab key={tab.value} label={tab.label} value={tab.value} />
                    ))}
                  </Tabs>
                </Box>
              )}
              <Box sx={styles.content}>
                {activeTab === "billing-types" && (
                  <BillingTypes
                    entityPath={`entities.${selected.id}`}
                    dropdownComponent={renderDropdownComponent()}
                    focusSection={billingSection}
                  />
                )}
                {activeTab === "ancillary-rates" && (
                  <MiscRates
                    entityPath={`entities.${selected.id}`}
                    dropdownComponent={renderDropdownComponent()}
                    readOnly={readOnly}
                    focusSection={billingSection}
                  />
                )}
                {activeTab === "advanced" && (
                  <TaxSettings />
                )}
              </Box>
            </DialogContent>
            {!readOnly && (
              <DialogActions sx={{ px: 3, pb: 2 }}>
                {isEditRatesMode ? (
                  <Button
                    onClick={handleSubmit}
                    variant="contained"
                    color="primary"
                    disabled={isSubmitting}
                    startIcon={isSubmitting && <CircularProgress size={22} />}
                  >
                    Save
                  </Button>
                ) : (
                  <>
                    {activeTab === "billing-types" && (
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => setActiveTab("ancillary-rates")}
                      >
                        Next
                      </Button>
                    )}
                    {activeTab === "ancillary-rates" && (
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => setActiveTab("advanced")}
                      >
                        Next
                      </Button>
                    )}
                    {activeTab === "advanced" && (
                      <Button
                        onClick={handleSubmit}
                        variant="contained"
                        color="primary"
                        disabled={isSubmitting}
                        startIcon={isSubmitting && <CircularProgress size={22} />}
                      >
                        Save
                      </Button>
                    )}
                  </>
                )}
              </DialogActions>
            )}
            {openCostCenterModal && !readOnly && (
              <CostCenterModal
                open={openCostCenterModal}
                onClose={() => setOpenCostCenterModal(false)}
                costCenter={{}}
                isNew={true}
                submitForm={() => {}}
                onSaved={cc => {
                  setSelectedId(cc.id);
                }}
              />
            )}
            {showDialog && (
              <UnsavedChangesDialog
                open={showDialog}
                onCancel={cancelDiscard}
                onDiscard={confirmDiscard}
                onSave={confirmSave}
                saving={saving}
              />
            )}
          </Dialog>
        );
      }}
    </Formik>
  );
}
